import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { toCanvas } from "html-to-image";
import { QRCodeSVG } from "qrcode.react";
import { api, showPin } from "@/lib/api";
import { badgeVcard } from "@/lib/vcard";

export const FIELD_LABELS = { logo: "GAMLS logo", header: "Header text", event: "Event name", name: "Participant name", category: "Category", ticket: "Ticket number", pin: "AHPC PIN",
  gamls_id: "GAMLS ID", region: "Region", facility: "Facility", phone: "Phone", email: "Email", footer: "Footer text", qr: "QR code" };
export const FONTS = ["Arial", "Helvetica", "Verdana", "Tahoma", "Trebuchet MS", "Georgia"];
export const DPIS = [203, 300, 600];

export const loadTemplate = () => api.get("/label-template").then((r) => r.data);

export const stickerData = (r, eventTitle = "") => ({ name: r.delegate_name || r.name, ticket_no: r.ticket_no, pin: r.pin, gamls_id: r.gamls_id, category: r.category_name || r.category,
  region: r.region, facility: r.facility, phone: r.phone, email: r.email, event: eventTitle || r.event_title || "" });

export const pageSize = (t) => (t.orientation === "portrait" ? [t.height_mm, t.width_mm] : [t.width_mm, t.height_mm]);

const INK = "#000000";

export const Sticker = ({ t, d, logoSrc = "/images/gamls-logo.png" }) => {
  const f = t.fields;
  const font = `${t.font || "Arial"}, Helvetica, sans-serif`;
  const center = t.align === "center";
  const line = (pt, extra = {}) => ({ fontSize: `${pt}pt`, lineHeight: 1.15, margin: 0, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: center ? "center" : "left", ...extra });
  const q = Math.max(5, Math.min(t.qr_size_mm, t.height_mm - 2 * t.padding_mm));
  const qr = f.qr && <div style={{ flex: "none", width: `${q}mm`, height: `${q}mm` }}><QRCodeSVG value={badgeVcard(d)} level="M" fgColor={INK} bgColor="#FFFFFF" style={{ width: "100%", height: "100%", display: "block" }} /></div>;
  const text = (k, v, pt, extra) => f[k] && v ? <p style={line(pt, extra)}>{v}</p> : null;
  return (
    <div className="thermal-label" style={{ width: `${t.width_mm}mm`, height: `${t.height_mm}mm`, padding: `${t.padding_mm}mm`, boxSizing: "border-box", overflow: "hidden", background: "#FFFFFF", color: INK,
      fontFamily: font, border: t.border ? `0.4mm solid ${INK}` : "none", display: "flex", alignItems: "center", gap: "2mm", flexDirection: t.qr_side === "left" ? "row-reverse" : "row" }} data-testid="sticker">
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "0.6mm", alignItems: center ? "center" : "stretch" }}>
        {(f.logo || f.header) && <div style={{ display: "flex", alignItems: "center", gap: "1.5mm", justifyContent: center ? "center" : "flex-start", maxWidth: "100%" }}>
          {f.logo && <img src={logoSrc} alt="" style={{ width: `${Math.max(4, t.text_size_pt * 0.6)}mm`, height: `${Math.max(4, t.text_size_pt * 0.6)}mm`, filter: "grayscale(100%) contrast(400%)" }} />}
          {f.header && <p style={line(t.text_size_pt, { fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" })}>{t.header_text}</p>}
        </div>}
        {text("event", d.event, t.text_size_pt, { fontWeight: 700 })}
        {f.name && <p style={line(t.name_size_pt, { fontWeight: 700, whiteSpace: "normal", maxHeight: `${t.name_size_pt * 2.4}pt` })}>{d.name}</p>}
        {text("category", d.category, t.text_size_pt, { fontWeight: 700 })}
        {f.ticket && <p style={line(t.text_size_pt + 1, { fontWeight: 700, letterSpacing: "0.03em", fontVariantNumeric: "tabular-nums" })}>{d.ticket_no || "No ticket"}</p>}
        {text("pin", d.pin && `PIN ${showPin(d.pin)}`, t.text_size_pt)}
        {text("gamls_id", d.gamls_id && `GAMLS ${d.gamls_id}`, t.text_size_pt)}
        {text("region", d.region, t.text_size_pt)}
        {text("facility", d.facility, t.text_size_pt)}
        {text("phone", d.phone, t.text_size_pt)}
        {text("email", d.email, t.text_size_pt)}
        {text("footer", t.footer_text, t.text_size_pt, { fontStyle: "italic" })}
      </div>
      {qr}
    </div>
  );
};

export const StickerPage = ({ t, d, logoSrc }) => {
  const [pw, ph] = pageSize(t);
  const turn = t.orientation === "portrait" ? { transform: "rotate(90deg) translateY(-100%)", transformOrigin: "top left" } : {};
  return (
    <div style={{ width: `${pw}mm`, height: `${ph}mm`, overflow: "hidden", background: "#FFFFFF", position: "relative" }} data-testid="sticker-page">
      <div style={{ width: "fit-content", ...turn }}><Sticker t={t} d={d} logoSrc={logoSrc} /></div>
    </div>
  );
};

const crcTable = Array.from({ length: 256 }, (_, n) => {
  let c = n;
  for (let k = 0; k < 8; k += 1) { c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; }
  return c >>> 0;
});
const crc32 = (bytes) => {
  let c = 0xffffffff;
  for (const b of bytes) { c = crcTable[(c ^ b) & 0xff] ^ (c >>> 8); }
  return (c ^ 0xffffffff) >>> 0;
};

// Adds a pHYs chunk so BarTender and other apps import the picture at its real size in mm.
const withDpi = (png, dpi) => {
  const ppm = Math.round(dpi / 0.0254);
  const body = new Uint8Array(13);
  const v = new DataView(body.buffer);
  body.set([0x70, 0x48, 0x59, 0x73]);
  v.setUint32(4, ppm);
  v.setUint32(8, ppm);
  body[12] = 1;
  const chunk = new Uint8Array(21);
  const cv = new DataView(chunk.buffer);
  cv.setUint32(0, 9);
  chunk.set(body, 4);
  cv.setUint32(17, crc32(body));
  const out = new Uint8Array(png.length + 21);
  out.set(png.slice(0, 33));
  out.set(chunk, 33);
  out.set(png.slice(33), 54);
  return out;
};

export const fileName = (name) => `${(name || "participant").trim().replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, " ") || "participant"}.png`;

export const downloadStickerImage = async (t, d) => {
  const [pw, ph] = pageSize(t);
  const host = document.createElement("div");
  Object.assign(host.style, { position: "fixed", left: "-10000px", top: "0", background: "#FFFFFF" });
  document.body.appendChild(host);
  const root = createRoot(host);
  try {
    flushSync(() => root.render(<StickerPage t={t} d={d} />));
    await Promise.all([...host.querySelectorAll("img")].map((i) => (i.complete ? null : new Promise((ok) => { i.onload = ok; i.onerror = ok; }))));
    const node = host.firstChild;
    const dpi = t.dpi || 300;
    const width = Math.round((pw / 25.4) * dpi);
    const height = Math.round((ph / 25.4) * dpi);
    const drawn = await toCanvas(node, { pixelRatio: width / node.offsetWidth, backgroundColor: "#FFFFFF", skipAutoScale: true, cacheBust: true });
    const exact = document.createElement("canvas");
    exact.width = width;
    exact.height = height;
    const ctx = exact.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(drawn, 0, 0, width, height);
    const bin = new Uint8Array(await (await new Promise((ok) => exact.toBlob(ok, "image/png"))).arrayBuffer());
    const blob = new Blob([withDpi(bin, dpi)], { type: "image/png" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fileName(d.name);
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    return { width, height, name: a.download };
  } finally {
    root.unmount();
    host.remove();
  }
};
